import * as Sentry from '@sentry/vue';

export interface CustomErrorOptions {
	description?: string;
	cause?: unknown;
	context?: Record<string, unknown>;
}

export class CustomError extends Error {
	public readonly description: string = '';
	public userMessage: string = '';
	private readonly options?: CustomErrorOptions;

	get message(): string {
		const description = this.options?.description ?? this.description;
		const cause = this.cause;

		const causeText =
			cause instanceof Error ? cause.message : String(cause ?? '');
		const causeMessage = causeText ? `(caused by: ${causeText})` : '';

		return `${description} ${causeMessage}`.trim();
	}

	protected constructor(options?: CustomErrorOptions) {
		super();

		// Don't set name here - let subclasses set it explicitly to avoid minification issues
		this.cause = options?.cause;
		this.options = options;

		if (options?.cause instanceof Error && options.cause.stack) {
			this.stack = `${this.stack}\n\nCaused by:\n${options.cause.stack}`;
		}
	}

	static create(options?: CustomErrorOptions): CustomError {
		const customError = new this(options);

		return customError;
	}

	static report(options?: CustomErrorOptions): CustomError {
		const customError = new this(options);
		customError.report();

		return customError;
	}

	report(): void {
		try {
			if (this.options?.context) {
				Sentry.setContext('context', this.options.context);
			}

			Sentry.captureException(this);
		} catch (error) {
			console.error(error);
		}
	}

	debug(): void {
		console.error('CustomError debug', this);
	}
}
